/*
	Aviso "la talla va aparte" (Esteban, 2026-10-21). Christy escribía la talla en el nombre ("Bata de Belleza S") y WAPP
	creaba un producto por talla. Esto solo AVISA: nunca cambia el nombre por su cuenta ni impide guardar.
	Para no avisar de más: solo mira la última palabra (o "Talla X" en cualquier parte), no confunde "Aceite 1 L" (litro),
	"Set de 3 Piezas" ni "Pincel Número 4", y los números solo cuentan como talla en calzado o con la palabra "talla".
*/

export interface TallaEnNombre {
	/** El nombre sin la talla: "Bata de Belleza". */
	base: string;
	/** La talla como la usa WAPP: "S", "XXL", "38". */
	size: string;
	/** El tipo de talla que le toca: letters | shoe. */
	sizeType: "letters" | "shoe";
}

// "XL", "XXXS", "2XL", "M"; a digit only goes before X ("1L" is a liter, not a size).
const LETTER = /^(?:(?:\d{1,2}X|X{1,5})?(?:S|L)|M)$/;
const SHOE_WORDS = /\b(zapato|zapatos|tenis|bota|botas|calzado|zapatilla|zapatillas|sandalia|sandalias|botin|botín|crocs)\b/i;
// "2XL" → "XXL" like the scale (up to XXXXL; from 5XL on the scale itself uses the number).
const NUMBERED: Record<string, string> = { "2XL": "XXL", "3XL": "XXXL", "4XL": "XXXXL", "2XS": "XXS", "3XS": "XXXS" };

const clean = (word: string) => word.replace(/^[([{"'«]+|[)\]}"'».,;:!?]+$/g, "");

export const tallaEnNombre = (name: string): TallaEnNombre | null => {
	const words = name.trim().split(/\s+/).filter(Boolean);
	if (words.length < 2) return null;

	// "Filipina Talla M Blanca" / "Zapato talla 38".
	const tallaAt = words.findIndex((word) => /^talla$/i.test(clean(word)));
	if (tallaAt >= 0 && tallaAt < words.length - 1) {
		const raw = clean(words[tallaAt + 1]).toUpperCase();
		const rest = words.filter((_, index) => index !== tallaAt && index !== tallaAt + 1).join(" ").trim();
		if (rest.length > 1 && LETTER.test(raw)) return { base: rest, size: NUMBERED[raw] || raw, sizeType: "letters" };
		if (rest.length > 1 && /^\d{2}(\.5)?$/.test(raw)) return { base: rest, size: raw, sizeType: "shoe" };
	}

	const last = clean(words[words.length - 1]);
	const before = words.slice(0, -1);
	const base = before.join(" ").replace(/[\s\-–·,]+$/, "").trim();
	if (base.length < 3 || before.length < 1) return null;
	const previous = clean(before[before.length - 1]);
	// "Aceite 1 L", "Botella 500 M": a number right before is a measure, not a size.
	if (/^\d+([.,]\d+)?$/.test(previous)) return null;

	const upper = last.toUpperCase();
	if (LETTER.test(upper)) {
		// A lone S/M/L needs at least two words before it ("Bata de Belleza M"), so "Plan M" or "Vitamina C" never trigger it.
		if (/^(S|M|L)$/.test(upper) && before.length < 2) return null;
		return { base, size: NUMBERED[upper] || upper, sizeType: "letters" };
	}
	// Shoes: "Zapato Antiderrapante Negro 38/39" or "… 38".
	if (SHOE_WORDS.test(base) && /^\d{2}(\/\d{2})?$/.test(last)) {
		return { base, size: last, sizeType: "shoe" };
	}
	return null;
};
